<script setup lang="ts">
import { SellingMethod, useApiGetValueAddedServices } from '~/apiClient';
import { INJECTION_KEYS } from '~/constants/injectionKeys';
import { isAuction, type SaleItemEntity } from '~/types/sale.type';

// PROPS
const { sale } = defineProps({
    sale: { type: Object as PropType<SaleItemEntity>, required: true },
});

// EMIT
const emit = defineEmits<{
    refetch: [];
}>();

const saleApiParam = computed(() => ({ [sale.sellingMethod === SellingMethod.Auction ? 'auctionId' : 'directSaleId']: sale.id }));

// COMPOSABLES
useSdpSeoMeta(toRef(() => sale));
const { isLoggedIn } = useAuthUtils();
const routeBaseName = useRouteBaseName();
const { resetTrackingReferralListName, trackSalesContactExitContactFormModal, syncTrackingGlobalSale } = useTracking();
const { salesContact, suspense: suspenseSalesContact } = useSalesContact({ sale: () => sale });
const mobileSaleInitialUi = useTemplateRef('mobileSaleInitialUi');
const mobileSaleInitialUiIsVisible = ref(true);
const { tt } = useTypedI18n();
const { data: valueAddedServices, suspense: suspenseValueAddedServices } = useApiGetValueAddedServices(saleApiParam);

await suspenseSalesContact();
await suspenseValueAddedServices();

provide(INJECTION_KEYS.SALES_CONTACT, salesContact);
provide(INJECTION_KEYS.VALUE_ADDED_SERVICES, valueAddedServices);

// DATA
const salesContactFormModalState = useModalState<boolean>();

// COMPOSABLES
const { isMonoVersion } = useSaleType(() => sale.type);

// METHODS
const onOpenSalesContactForm = async (): Promise<void> => {
    const formSubmitted = await salesContactFormModalState.open();
    formSubmitted || trackSalesContactExitContactFormModal();
};

const setupMobileSaleUiVisibility = (): void => {
    const headerHeight = useCssVar('--header-total-height');
    const rootMargin = headerHeight.value && `-${headerHeight.value.trim()}`;

    // TODO: Could be replaced with useElementVisibility when initialValue will be available (see: https://github.com/vueuse/vueuse/issues/5158)
    useIntersectionObserver(
        mobileSaleInitialUi,
        ([entry]) => {
            if (entry) mobileSaleInitialUiIsVisible.value = entry.isIntersecting;
        },
        { rootMargin }
    );
};

// WATCHERS
watch(isLoggedIn, (value, oldValue) => {
    value !== oldValue && emit('refetch');
});

syncTrackingGlobalSale(toRef(() => sale));

// LIFECYCLE
onBeforeRouteLeave((to, _from, next) => {
    if (!isSdpRoute(routeBaseName(to))) resetTrackingReferralListName();
    next();
});

onMounted(() => {
    setupMobileSaleUiVisibility();
});
</script>

<template>
    <article>
        <SdpToBeApprovedToolbarAlert :sale="sale" />
        <BaseWidthContainer class="gap-7 py-6 lg:grid lg:grid-cols-3">
            <div class="flex flex-col gap-y-5 md:gap-y-9 lg:col-span-2">
                <SdpMonoGallery v-if="isMonoVersion" :sale="sale" />
                <section class="flex flex-col gap-y-3">
                    <SdpTitleSection :sale="sale" />
                    <SdpKeyFeaturesBadges :sale="sale" />
                </section>

                <div ref="mobileSaleInitialUi" class="relative space-y-6 lg:hidden">
                    <SdpSalePanel :sale="sale" />
                    <SdpSocialProof :sale="sale" />
                    <SalesContactCard v-if="salesContact" :sales-contact="salesContact" @open-contact-form="onOpenSalesContactForm" />
                </div>
                <SdpDepotVisitBookingAlert :sale="sale" />
                <SdpAssets :sale="sale" />
            </div>

            <div class="col-span-1 hidden lg:block">
                <div class="sticky top-[calc(var(--header-total-height)+24px)] space-y-6">
                    <SdpSalePanel :sale="sale" />
                    <SdpSocialProof :sale="sale" />
                    <SalesContactCard v-if="salesContact" :sales-contact="salesContact" @open-contact-form="onOpenSalesContactForm" />
                    <SdpReviews view="desktop" />
                </div>
            </div>
        </BaseWidthContainer>

        <ClientOnly>
            <div
                class="sticky bottom-0 z-20 border-b border-neutral-300 opacity-0 transition-opacity duration-300 lg:hidden"
                :class="mobileSaleInitialUiIsVisible ? 'opacity-0' : 'opacity-100'"
            >
                <SdpSalesContactFloatingActions v-if="salesContact" :sales-contact="salesContact" @open-contact-form="onOpenSalesContactForm" />

                <BaseBottomSheet :a11y-title="tt('sdp.bottomSheet.sheetTitle')" overlay>
                    <template #compact="{ open }">
                        <SdpBottomSheetCompact :sale="sale" @trigger-open="open" />
                    </template>

                    <template #expanded>
                        <SdpSalePanel :sale="sale" embedded />
                    </template>
                </BaseBottomSheet>
            </div>
        </ClientOnly>

        <BaseWidthContainer class="py-6">
            <SdpRelatedSales :sale="sale" />
        </BaseWidthContainer>

        <MktgWhyFleequid class="border-b border-white/15" />

        <BaseWidthContainer class="bg-primary-950 border-b border-white/15 py-[60px] lg:hidden">
            <SdpReviews view="mobile" />
        </BaseWidthContainer>
    </article>

    <AdpCountdownAudio v-if="isAuction(sale)" :auction="sale" />
    <SalesContactFormModal
        v-if="salesContact"
        v-model="salesContactFormModalState.isOpen"
        :sales-contact="salesContact"
        @success="salesContactFormModalState.confirm(true)"
    />
</template>
